// Sugalarm — web Reports (cycle 181). Replaces the cycle-170 placeholder.
//
// WEB FRAME ONLY (guarded → /now elsewhere). Generate/download buttons for
// two branded, server-rendered PDF reports for the VIEWED (active) member:
//   • AGP clinical report (for the endocrinologist), and
//   • Alarm & response report (Sugalarm's distinctive artifact).
// The PDFs are rendered server-side (pure-Python WeasyPrint, off the event
// loop) and downloaded via the existing authed-fetch → blob path. No PHI
// touches the URL; the report is scoped to the active member by the API.

import { useState } from "react";
import { Navigate } from "react-router-dom";

import * as reportsApi from "../../api/reports";
import { Button } from "../../components/ui/Button";
import { Tabs } from "../../components/ui/Tabs";
import type { FrameVariant } from "../../hooks/useFrameVariant";
import styles from "./ReportsScreen.module.css";

type RangeId = "14" | "30" | "90";
const RANGES: { id: RangeId; label: string; days: number }[] = [
  { id: "14", label: "14 days", days: 14 },
  { id: "30", label: "30 days", days: 30 },
  { id: "90", label: "90 days", days: 90 },
];

interface Props {
  variant: FrameVariant;
  memberName: string | null;
}

type Kind = "agp" | "alarms";

export function ReportsScreen({ variant, memberName }: Props) {
  const [rangeId, setRangeId] = useState<RangeId>("14");
  const [busy, setBusy] = useState<Kind | null>(null);
  const [error, setError] = useState<string | null>(null);

  const days = RANGES.find((r) => r.id === rangeId)?.days ?? 14;

  if (variant !== "web") return <Navigate to="/now" replace />;

  const run = async (kind: Kind) => {
    setError(null);
    setBusy(kind);
    try {
      const res =
        kind === "agp"
          ? await reportsApi.downloadAgpReport(days)
          : await reportsApi.downloadAlarmReport(days);
      if (!res.ok) {
        setError(
          res.status === 503
            ? "Report generation is temporarily unavailable. Please try again later."
            : res.message || `Couldn't generate the report (error ${res.status}).`,
        );
      }
    } catch {
      setError("Network error while generating the report.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.inner}>
        <header className={styles.header}>
          <div>
            <h2 className={styles.title}>Reports</h2>
            <p className={styles.sub}>
              Branded PDF reports for {memberName ?? "the viewed member"}.
              Observational — not for dosing.
            </p>
          </div>
          <div className={styles.rangeTabs}>
            <Tabs<RangeId>
              tabs={RANGES.map((r) => ({ id: r.id, label: r.label }))}
              activeId={rangeId}
              onChange={setRangeId}
            />
          </div>
        </header>

        {error ? <div className={styles.error}>{error}</div> : null}

        <div className={styles.grid}>
          <section className={styles.card}>
            <h3 className={styles.cardTitle}>AGP clinical report</h3>
            <p className={styles.cardBody}>
              The clinician-standard Ambulatory Glucose Profile: the
              percentile chart over a typical day, plus Time-in-Range, GMI,
              CV, and your lows &amp; highs. Designed to share with your care
              team.
            </p>
            <Button onClick={() => run("agp")} disabled={busy !== null}>
              {busy === "agp" ? "Generating…" : `Download AGP (${days}d)`}
            </Button>
          </section>

          <section className={styles.card}>
            <h3 className={styles.cardTitle}>Alarm &amp; response report</h3>
            <p className={styles.cardBody}>
              Every alarm episode over the range — severity, time, how it
              resolved, response times, and which channels were notified.
              Sugar Alarm's distinctive record of what happened and how it was
              handled.
            </p>
            <Button onClick={() => run("alarms")} disabled={busy !== null}>
              {busy === "alarms" ? "Generating…" : `Download alarms (${days}d)`}
            </Button>
          </section>
        </div>

        <p className={styles.foot}>
          Reports cover the selected range for the member currently in view.
          Generated on the server and downloaded directly — nothing is stored.
        </p>
      </div>
    </div>
  );
}
